import { useQuery } from '@tanstack/react-query';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { Dialog, DialogRoot, GitBookLoader, LoadingPanel, toast } from '@gitbook/spine-react';
import { useEventCallback, useLoadingState } from '@gitbook/util-react-hooks';

import { GitSyncInstallationDialogContent, GitSyncTargetStatusProvider } from '../../../Components';
import { useNavigation } from '../../../navigation';
import { apiClient, apiQuery, apiQueryClient, getSiteSpacesFromStructure } from '../../../state';
import { useCreateSpaceInEmptySite } from '../../Site/components';

export function SiteOnboardingGitSyncDialog(props: {
    site: api.Site;
    organization: api.Organization;
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
}) {
    const { site, organization, isOpen, onOpenChange } = props;
    const { navigate } = useNavigation();

    const createSpaceInEmptySite = useCreateSpaceInEmptySite({ organization, site });
    const { data: organizationContent } = useQuery(
        apiQuery('getOrganizationAllContent', { organizationId: organization.id })
    );
    const gitSyncTargetSpace = React.useMemo(() => {
        const siteContent = organizationContent?.sites.find((entry) => entry.site.id === site.id);
        const siteSpace = siteContent ? getSiteSpacesFromStructure(siteContent.structure)[0] : null;
        return siteSpace?.space ?? null;
    }, [organizationContent, site.id]);
    const hasRequestedGitSyncTargetRef = React.useRef(false);
    const [isPreparingGitSyncTarget, wrapPreparingGitSyncTarget] = useLoadingState(false);

    const prepareGitSyncTarget = useEventCallback(async () => {
        if (
            gitSyncTargetSpace ||
            isPreparingGitSyncTarget ||
            hasRequestedGitSyncTargetRef.current
        ) {
            return;
        }

        hasRequestedGitSyncTargetRef.current = true;

        const didCreateSpace = await toast.catch(async () => {
            await wrapPreparingGitSyncTarget(async () => {
                const space = await createSpaceInEmptySite({
                    title: 'Docs',
                    source: 'gitsync',
                });
                await apiClient.siteSpaces.addSpaceToSite(organization.id, site.id, {
                    draft: false,
                    spaceId: space.id,
                });
                await apiQueryClient.refetchQueries({
                    queryKey: apiQuery('getOrganizationAllContent', {
                        organizationId: organization.id,
                    }).queryKey,
                    exact: true,
                });
            });

            return true;
        }, 'Error while preparing Git Sync');

        if (!didCreateSpace) {
            hasRequestedGitSyncTargetRef.current = false;
            onOpenChange(false);
        }
    });

    React.useEffect(() => {
        if (!isOpen) {
            hasRequestedGitSyncTargetRef.current = false;
            return;
        }

        if (isOpen) {
            void prepareGitSyncTarget();
        }
    }, [isOpen, prepareGitSyncTarget]);

    return (
        <DialogRoot isOpen={isOpen} onOpenChange={onOpenChange}>
            <Dialog size={{ width: 1024, height: 640 }} className="flex flex-col">
                {gitSyncTargetSpace ? (
                    <GitSyncTargetStatusProvider target={gitSyncTargetSpace}>
                        <React.Suspense fallback={<LoadingPanel size="medium" />}>
                            <GitSyncInstallationDialogContent
                                target={gitSyncTargetSpace}
                                completeLabel="Go to GitBook"
                                onUnsavedChangesChange={() => {}}
                                onReconfigure={() => {}}
                                onComplete={() => {
                                    onOpenChange(false);
                                    navigate({
                                        screen: {
                                            type: 'space',
                                            organization: organization.id,
                                            space: gitSyncTargetSpace.id,
                                            sideSheet: {
                                                type: 'gitsync',
                                                tab: 'status',
                                            },
                                        },
                                    });
                                }}
                            />
                        </React.Suspense>
                    </GitSyncTargetStatusProvider>
                ) : (
                    <GitSyncTargetPrepLoadingState />
                )}
            </Dialog>
        </DialogRoot>
    );
}

function GitSyncTargetPrepLoadingState() {
    return (
        <div className="flex flex-1 flex-col items-center justify-center gap-4">
            <GitBookLoader />
            <div className="text-ui-heading-small">Preparing Git Sync...</div>
        </div>
    );
}
